import { Location, TransitionPromptHook, UnregisterCallback } from 'history';
import { pull } from 'lodash';
import history from './history';

/**
 * This is the result `history` expects the TransitionPromptHook to return
 *
 * - `false` means block without prompt
 * - `string` means block with this message
 * - `undefined` (or anything else) means don't block
 */
type TransitionPromptResult = false | string | void;

/**
 * A predicate function used in Small Improvements code.
 *
 * It can return
 * - a `string` reason to block
 * - `true` to block with a generic reason
 * - `false` to not block.
 */
interface ShouldBlock {
    (currentLocation?: Location, newLocation?: Location): string | boolean;
}

/**
 * This simply wraps `history.block`, accounting for test runs where `history` might be undefined
 */
function historyBlock(prompt: TransitionPromptHook): UnregisterCallback {
    if (history) {
        return history.block(prompt);
    } else {
        return () => undefined;
    }
}

const blockers: ShouldBlock[] = [];
let unblockHistory: null | (() => void) = null;

export function block(shouldBlock: ShouldBlock) {
    blockers.push(shouldBlock);
    if (!unblockHistory) {
        unblockHistory = historyBlock(promptHook);
        window.addEventListener('beforeunload', handleUnload);
    }
    return function unblock() {
        pull(blockers, shouldBlock);
        // @ts-ignore We're not using this value again. Nullifying is a safeguard against memleaks
        shouldBlock = null;
        if (blockers.length === 0) {
            unblockHistory!();
            unblockHistory = null;
            window.removeEventListener('beforeunload', handleUnload);
        }
    };
}

function promptHook(newLocation?: Location): TransitionPromptResult {
    const currentLocation = history?.location;
    const blocking = blockers.reduce<boolean | string>(
        (result, shouldBlock) => result || shouldBlock(currentLocation, newLocation),
        false
    );
    if (blocking === true) {
        return 'Are you sure you want to leave?';
    } else if (blocking === false) {
        return undefined;
    } else {
        return blocking;
    }
}

function handleUnload(event: BeforeUnloadEvent) {
    const result = promptHook();
    if (result === false || typeof result === 'string') {
        // Note: The actual text here doesn't matter. Browsers will render a fixed text for the beforeUnload event
        event.returnValue = result;
        return result;
    }
}
